import { useAuthStore } from "@/stores";
import type { ApiError } from "@/types";

// =============================================================================
// Types
// =============================================================================

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestConfig {
	method?: HttpMethod;
	body?: unknown;
	params?: Record<string, string | number | boolean | undefined>;
	headers?: Record<string, string>;
}

// =============================================================================
// Custom Error
// =============================================================================

export class ApiClientError extends Error {
	code: string;
	status: number;
	details?: Record<string, unknown>;
	timestamp: string;

	constructor(error: ApiError, status: number) {
		super(error.message);
		this.name = "ApiClientError";
		this.code = error.code;
		this.status = status;
		this.details = error.details;
		this.timestamp = error.timestamp;
	}
}

// =============================================================================
// API Client
// =============================================================================

// Use environment variable for API base URL, fallback to /api for MSW
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

function buildUrl(
	endpoint: string,
	params?: Record<string, string | number | boolean | undefined>,
): string {
	const url = new URL(`${BASE_URL}${endpoint}`, window.location.origin);

	if (params) {
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) {
				url.searchParams.append(key, String(value));
			}
		}
	}

	return url.toString();
}

function getAuthHeaders(): Record<string, string> {
	const token = useAuthStore.getState().token;
	if (token) {
		return { Authorization: `Bearer ${token}` };
	}
	return {};
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		let errorData: ApiError;

		try {
			errorData = await response.json();
		} catch {
			errorData = {
				code: "UNKNOWN_ERROR",
				message: response.statusText || "Error desconocido",
				timestamp: new Date().toISOString(),
			};
		}

		// Handle 401 - Unauthorized
		if (response.status === 401) {
			useAuthStore.getState().logout();
		}

		throw new ApiClientError(errorData, response.status);
	}

	// Handle 204 No Content
	if (response.status === 204) {
		return {} as T;
	}

	return response.json();
}

async function request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
	const { method = "GET", body, params, headers = {} } = config;

	const url = buildUrl(endpoint, params);

	const requestHeaders: Record<string, string> = {
		"Content-Type": "application/json",
		...getAuthHeaders(),
		...headers,
	};

	const fetchConfig: RequestInit = {
		method,
		headers: requestHeaders,
	};

	if (body && method !== "GET") {
		fetchConfig.body = JSON.stringify(body);
	}

	const response = await fetch(url, fetchConfig);
	return handleResponse<T>(response);
}

// =============================================================================
// Exported Methods
// =============================================================================

export const apiClient = {
	get: <T>(endpoint: string, params?: RequestConfig["params"]) =>
		request<T>(endpoint, { method: "GET", params }),

	post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: "POST", body }),

	put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: "PUT", body }),

	patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: "PATCH", body }),

	delete: <T>(endpoint: string) => request<T>(endpoint, { method: "DELETE" }),
};
